import type { SignoffHistoryItem, SignoffStatus } from '@/lib/api/requirements-signoff';
import type { EngagementRequirement } from '@/lib/api/engagement-requirements';
import { formatDay, plural } from '@/components/discovery/discovery-logic';
import type { StatusChipTone } from '@/components/ui/status-chip';

/**
 * Words and rules of the sign-off screen (T-0191; prototype `#signoff`,
 * E12-F11, Decisions 46, 53.11, 54.37–54.41). The server computes the
 * checklist and checks every rule again; these only decide what the
 * screen says and which buttons it offers.
 */

/** A waiver needs a written reason of at least this many characters (E12-F11 §4). */
export const WAIVER_MIN_CHARS = 20;

export type SignoffTone = Extract<
  StatusChipTone,
  'healthy' | 'amber' | 'critical' | 'at-risk' | 'neutral' | 'unassessed'
>;

export interface SignoffHeadline {
  tone: SignoffTone;
  chip: string;
  /** The Selection → Requirements header's two or three words (Decision 54.40). */
  short: string;
}

/** Open sign-offs block a new request or waiver (the partial unique index). */
export function isOpen(s: Pick<SignoffStatus, 'state'>): boolean {
  return s.state === 'requested' || s.state === 'signed' || s.state === 'waived';
}

export function openConditions(s: Pick<SignoffStatus, 'checklist'>): number {
  return s.checklist.filter((c) => !c.ok).length;
}

export function checklistComplete(s: Pick<SignoffStatus, 'checklist'>): boolean {
  return s.checklist.length > 0 && openConditions(s) === 0;
}

/** Request and waive need the complete checklist and nothing open (Decision 53.11). */
export function canAsk(s: Pick<SignoffStatus, 'state' | 'checklist'>): boolean {
  return !isOpen(s) && checklistComplete(s);
}

export function signoffHeadline(s: Pick<SignoffStatus, 'state' | 'checklist'>): SignoffHeadline {
  switch (s.state) {
    case 'signed':
      return { tone: 'healthy', chip: 'Signed', short: 'Signed' };
    case 'requested':
      return { tone: 'amber', chip: 'Awaiting signature', short: 'Awaiting signature' };
    case 'waived':
      return { tone: 'at-risk', chip: 'Waived', short: 'Waived' };
    case 'reopened':
      return { tone: 'critical', chip: 'Reopened', short: 'Reopened' };
    case 'changes_requested':
      return { tone: 'critical', chip: 'Changes requested', short: 'Changes requested' };
    default: {
      const open = openConditions(s);
      return open === 0 && s.checklist.length > 0
        ? { tone: 'neutral', chip: 'Ready to request', short: 'Ready to request' }
        : {
            tone: 'unassessed',
            chip: 'Not ready',
            short: `${open} of ${s.checklist.length} conditions open`,
          };
    }
  }
}

/** "6 Oct 2026 09:12", in the reader's time zone. */
export function formatDayTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${formatDay(iso)} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

/** The sentence under the title. */
export function signoffStateLine(s: SignoffStatus): string {
  const who = s.signatory?.full_name ?? 'the signatory';
  switch (s.state) {
    case 'signed': {
      if (!s.signed) return 'Signed';
      const [day, time] = [formatDay(s.signed.at), formatDayTime(s.signed.at).split(' ').at(-1)];
      return `Signed by ${s.signed.name}${s.signatory?.title ? `, ${s.signatory.title}` : ''}, on ${day} at ${time}`;
    }
    case 'requested': {
      const parts = [
        s.snapshot ? `Sent to ${who} on ${formatDay(s.snapshot.captured_at)}` : `Sent to ${who}`,
      ];
      if (s.link?.opened_at) parts.push(`opened ${formatDayTime(s.link.opened_at)}`);
      if (s.link?.expires_at) parts.push(`link expires ${formatDay(s.link.expires_at)}`);
      return parts.join(' · ');
    }
    case 'waived':
      return s.waiver ? `Waived by ${s.waiver.by.name} on ${formatDay(s.waiver.at)}` : 'Waived';
    case 'reopened':
      return 'Requirements changed after sign-off. Sign-off must be requested again.';
    case 'changes_requested':
      return `${who} asked for changes instead of signing. Review them, then request sign-off again.`;
    default: {
      const open = openConditions(s);
      return open === 0 && s.checklist.length > 0
        ? 'Everything below is done. Request the client signatory’s sign-off.'
        : `${open} of ${s.checklist.length} conditions still open`;
    }
  }
}

/** The first name, for "See what Dana sees". */
export function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] ?? fullName;
}

/** The request dialog's opening message, as the prototype words it. */
export function defaultSignoffMessage(fullName: string): string {
  return `${firstName(fullName)}, the requirements are ready for your sign-off. Each one shows where it came from. Please read the packet and sign, or tell us what should change.`;
}

export interface CriticalityCounts {
  total: number;
  modules: number;
  by: Record<'critical' | 'desired' | 'not_required' | 'not_applicable', number>;
}

/** What the client signs: active requirements, counted as the E11 header counts them. */
export function criticalityCounts(
  rows: ReadonlyArray<Pick<EngagementRequirement, 'status' | 'module_id' | 'criticality'>>,
): CriticalityCounts {
  const live = rows.filter((r) => r.status !== 'removed');
  const by: CriticalityCounts['by'] = {
    critical: 0,
    desired: 0,
    not_required: 0,
    not_applicable: 0,
  };
  for (const r of live) {
    if (r.criticality in by) by[r.criticality as keyof typeof by] += 1;
  }
  // A requirement with no library module (Not mapped) is in no module.
  const modules = new Set(live.flatMap((r) => (r.module_id ? [r.module_id] : []))).size;
  return { total: live.length, modules, by };
}

const FIELD_WORDS: Record<string, string> = {
  text: 'text edited',
  criticality: 'criticality changed',
  status: 'removed or restored',
  subcategory: 'subcategory changed',
  module: 'module changed',
};

/**
 * What reopened sign-off, in words (the reopen triggers' record, E12-F11
 * §2): "AP-C01 text edited", "PAY-037 added", "E-031 withdrawn, cited by 2
 * requirements". `more` counts what the record left out (it keeps 50).
 */
export function reopenedChangesText(changes: unknown): { items: string[]; more: number } {
  if (!changes || typeof changes !== 'object') return { items: [], more: 0 };
  const c = changes as {
    cause?: unknown;
    count?: unknown;
    number?: unknown;
    changes?: unknown;
  };
  const count = typeof c.count === 'number' ? c.count : 0;
  if (typeof c.cause === 'string' && c.cause.startsWith('cited_evidence_')) {
    const what = c.cause.slice('cited_evidence_'.length);
    const n = typeof c.number === 'string' ? c.number : 'Cited evidence';
    return { items: [`${n} ${what}, cited by ${plural(count, 'requirement')}`], more: 0 };
  }
  if (c.cause === 'requirement_evidence_added') {
    return { items: [`evidence linked to ${plural(count, 'requirement')}`], more: 0 };
  }
  const rows = Array.isArray(c.changes) ? (c.changes as Array<Record<string, unknown>>) : [];
  const items = rows.map((r) => {
    const number = typeof r['number'] === 'string' ? r['number'] : 'A requirement';
    if (r['change'] === 'added') return `${number} added`;
    const fields = Array.isArray(r['fields']) ? (r['fields'] as unknown[]) : [];
    const words = fields.map((f) => FIELD_WORDS[String(f)] ?? `${String(f)} changed`);
    return words.length ? `${number} ${words.join(' and ')}` : `${number} changed`;
  });
  return { items, more: Math.max(0, count - items.length) };
}

const HISTORY_STATE: Record<SignoffHistoryItem['state'], string> = {
  requested: 'awaiting signature',
  signed: 'signed',
  waived: 'waived',
  changes_requested: 'the client asked for changes',
  reopened: 'reopened by a later change',
  cancelled: 'cancelled',
};

/** One line of the history: who asked or waived, and where it ended up. */
export function historyText(h: SignoffHistoryItem): string {
  const who = h.by?.name ?? 'Unknown user';
  if (h.kind === 'waiver') {
    return h.state === 'waived'
      ? `Waived by ${who}`
      : `Waived by ${who}: ${HISTORY_STATE[h.state]}`;
  }
  return `Sign-off requested by ${who}: ${HISTORY_STATE[h.state]}`;
}

/** The history dot's posture. */
export function historyTone(
  h: SignoffHistoryItem,
): 'healthy' | 'at-risk' | 'critical' | 'unassessed' {
  switch (h.state) {
    case 'signed':
    case 'requested':
      return 'healthy';
    case 'waived':
      return 'at-risk';
    case 'cancelled':
      return 'unassessed';
    default:
      return 'critical';
  }
}
